import React, { useCallback, useEffect, useState } from 'react'
import { Card, Space, Button, Empty } from 'antd'
import Table, { type ColumnsType } from 'antd/es/table'
import { type AxiosError } from 'axios'
import { setLoader } from 'Store/slices/commonSlice'
import { useAppDispatch } from 'Hooks/ReduxHook'
import { DeleteOutlined } from '@ant-design/icons'
import { toast } from 'sonner'
import Http, { type ApiErrorData } from 'Util/Http'
import { type IEmergency } from 'Util/Interface'

const EMERGENCY_LIST_ENDPOINTS = ['/emergency', '/health-reports', '/healthReports']

const normalizeEmergencyList = (payload: unknown): IEmergency[] => {
  if (Array.isArray(payload)) {
    return payload as IEmergency[]
  }
  if (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)) {
    return (payload as { data: IEmergency[] }).data
  }
  return []
}

const Emergency: React.FC = () => {
  const [tableData, setTableData] = useState<IEmergency[]>([])

  const dispatch = useAppDispatch()

  const fetch = useCallback(async () => {
    try {
      dispatch(setLoader(true))
      let records: IEmergency[] = []

      for (const endpoint of EMERGENCY_LIST_ENDPOINTS) {
        try {
          const response = await Http(endpoint)
          const isOk = (response.data?.status === true || response.data?.status === 'success') && response?.data?.code === 200
          if (isOk) {
            const list = normalizeEmergencyList(response.data?.data)
            if (list.length > 0) {
              records = list
              break
            }
          }
        } catch {
          // try next endpoint
        }
      }

      setTableData(records)
      dispatch(setLoader(false))
    } catch (error: any) {
      const err = error as AxiosError<ApiErrorData>
      toast.error(err.response?.data?.message ?? 'Something Went Wrong')
      setTableData([])
      dispatch(setLoader(false))
    }
  }, [dispatch])

  useEffect(() => {
    void fetch()
  }, [fetch])

  const handleDelete = async (params: IEmergency) => {
    try {
      const response = await Http(`/emergency/${String(params.id)}`, {
        method: 'DELETE',
      })
      if (response.data?.status && response?.data?.code === 200) {
        toast.success(response?.data?.message)
        void fetch()
      } else {
        toast.error(response?.data?.message ?? 'Failed to delete record')
      }
    } catch (error) {
      const err = error as AxiosError<ApiErrorData>
      toast.error(err.response?.data?.message ?? 'Something Went Wrong')
    }
  }

  const columns: ColumnsType<IEmergency> = [
    {
      title: 'Id',
      dataIndex: 'index',
      key: 'id',
      render: (_value, _item, index) => index + 1,
    },
    {
      title: 'Patient Name',
      key: 'patientName',
      render: (_value, record) => record.patientName ?? record.title ?? '-',
    },
    {
      title: 'Blood Group',
      key: 'bloodGroup',
      render: (_value, record) => record.bloodGroup ?? '-',
    },
    {
      title: 'Relation',
      dataIndex: 'relation',
      key: 'relation',
      render: (value) => value ?? '-',
    },
    {
      title: 'Hospital Address',
      key: 'hospitalAddress',
      render: (_value, record) => record.hospitalAddress ?? '-',
    },
    {
      title: 'Mobile No',
      key: 'mobileNo',
      render: (_value, record) => record.mobileNo ?? '-',
    },
    {
      title: 'Comment',
      key: 'message',
      render: (_value, record) => record.message ?? '-',
    },
    {
      title: 'City',
      key: 'city',
      render: (_value, record) => record.city ?? '-',
    },
    {
      title: 'Action',
      dataIndex: 'actions',
      width: '15%',
      render: (_text, record: IEmergency) => {
        return (
          <Space>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                void handleDelete(record)
              }}
            >
              Delete
            </Button>
          </Space>
        )
      },
    },
  ]

  return (
    <>
      <div className="content-wrapper">
        <Card
          className="card-wrapper"
          title={
            <div className="row justify-between">
              <div>
                <h3>Emergency List</h3>
              </div>
            </div>
          }
          style={{ padding: '1.25rem 1.25rem 0' }}
        >
          <Table
            columns={columns}
            dataSource={tableData}
            rowKey={(dataIndex: IEmergency) => dataIndex?.id}
            scroll={{ x: 800 }}
            bordered
            size="middle"
            locale={{
              emptyText: <Empty description="No emergency records found. Submissions from the mobile app will appear here when available." />,
            }}
          />
        </Card>
      </div>
    </>
  )
}

export default Emergency
